import * as React from 'react';
import { StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { EVENTS } from 'src/constants/events';
import { WINDOW_SIZES } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import { appStore } from 'src/mobx/appStore';
import { cartStore } from 'src/mobx/cartStore';
import { productsStore } from 'src/mobx/productsStore';
import { requests } from 'src/requests';
import logger from 'src/services/logger';
import { IProductInsideCombo } from 'src/types/combo';
import {
  IProduct,
  ICashbackInfo,
  IProductExclusion,
  IProductModifier,
  IProductSize,
  PRODUCT_TYPE,
} from 'src/types/products';
import { getTranslate } from 'src/utils/formatter';
import {
  calcProductCashbackAmount,
  flatten,
  getFirstCorrectSize,
  getRestaurantName,
  isSameComboItems,
  isSameComboItemsSizeGroup,
  setMetaTags,
} from 'src/utils/helpers';
import { calculateProductPrice } from 'src/utils/math';
import { validateMultiChoiseModifier, validateSingleChoiseModifier } from 'src/utils/validator';
import Modal from 'src/view/components/Controls/Modal/Modal';
import ResponsiveContent from 'src/view/components/ResponsiveContent/ResponsiveContent';
import ProductDeleteModal from './components/ProductDeleteModal/ProductDeleteModal';
import DealComboItemsBlock from './containers/DealComboItemsBlock/DealComboItemsBlock';
import MealComboItemsBlock from './containers/MealComboItemsBlock/MealComboItemsBlock';
import ProductImagesBlock from './containers/ProductImagesBlock/ProductImagesBlock';
import ProductInfoBlock from './containers/ProductInfoBlock/ProductInfoBlock';
import ProductSelectorsBlock from './containers/ProductSelectorsBlock/ProductSelectorsBlock';
import ProductSubmitBlock from './containers/ProductSubmitBlock/ProductSubmitBlock';
import { branchStore } from 'src/mobx/barnchStore';
import './style.scss';
import { TTranslate } from 'src/types/main';

interface LocationState {
  product: IProduct | undefined;
  updateProduct?: boolean | undefined;
}

export interface IProductScreenProps extends RouteComponentProps<{ id: string }, StaticContext, LocationState> {}

export interface IProductScreenState {
  product: IProduct | undefined;
  cashbackInfoData: ICashbackInfo | null;
  cashbackInfoLoading: boolean;
  isOpen: boolean;
  loading: boolean;
  errors: IProductModifier['id'][];
  needScrollToError: boolean;
  deleteModal: boolean;
}

export interface IProductCashbackInfo {
  name: TTranslate | undefined;
  amount: number;
  description: TTranslate | undefined;
  loading: boolean;
  visible: boolean;
}

export default class ProductScreen extends React.Component<IProductScreenProps, IProductScreenState> {
  retrieveLocationState = (): IProduct | undefined => {
    const { state } = this.props.location;
    if (state && state.product) {
      return state.product;
    }
    return undefined;
  };

  state: IProductScreenState = {
    isOpen: false,
    product: this.retrieveLocationState(),
    cashbackInfoData: null,
    cashbackInfoLoading: false,
    loading: false,
    errors: [],
    needScrollToError: false,
    deleteModal: false,
  };

  componentDidMount = () => {
    this.setState({ isOpen: true });
    if (!this.state.product) {
      this.fetchProduct();
    } else {
      this.validateModifiers();
      this.logEvent(this.state.product);
      setMetaTags({
        title: `${getRestaurantName()} - ${getTranslate(this.state.product?.name)}`,
        description: `${getTranslate(this.state.product?.description)}`,
        keywords: `${this.getKeywords(this.state.product)}`,
        'og:title': `${getRestaurantName()} - ${getTranslate(this.state.product?.name)}`,
        'og:type': 'website',
        'og:image': `${this.state.product?.image}`,
        'og:description': `${getTranslate(this.state.product?.description)}`,
        'twitter:title': `${getRestaurantName()} - ${getTranslate(this.state.product?.name)}`,
        'twitter:description': `${getTranslate(this.state.product?.description)}`,
        'twitter:image': `${this.state.product?.image}`,
      });

      this.fetchCashbackInfo();

      if (this.state.product.type === PRODUCT_TYPE.MEAL_COMBO) this.setDefaultSelectedItems();
    }
  };

  componentWillUnmount = () => {
    setMetaTags({
      title: `${getRestaurantName()}`,
      description: `${getRestaurantName()}`,
      'og:title': `${getRestaurantName()}`,
      'og:description': `${getRestaurantName()}`,
      'og:type': 'website',
      'og:image': `${appStore.settings?.img_logo}`,
      'twitter:card': 'summary',
      'twitter:site': `${appStore.settings?.domain}`,
      'twitter:title': `${getRestaurantName()}`,
      'twitter:image': `${appStore.settings?.img_logo}`,
      'twitter:domain': `${appStore.settings?.domain}`,
    });
  };

  logEvent = (product: IProduct) => {
    logger.logEvent(EVENTS.PRODUCT_OPENED, { product });
  };

  getKeywords = (product: IProduct) => {
    const keywords = [];
    const modifiers = product.modifiers.map((el) => el.options);
    const category = productsStore.categories.find((category) => category.id === product?.category_id);
    keywords.push(getRestaurantName());

    if (category) keywords.push(getTranslate(category.name));

    product.modifiers.forEach((el) => keywords.push(getTranslate(el.name)));
    flatten(modifiers).forEach((el) => keywords.push(getTranslate(el.name)));

    return `${keywords.join(', ')}`;
  };

  closeModal = () => {
    // If it is SMS pages and cart have product
    if (cartStore.isSMS && cartStore.products.length) {
      this.props.history.push(SCREENS.SMSPAYMENT());
    } else {
      this.props.history.push(SCREENS.ROOT());
    }
  };

  openDeleteModal = () => {
    this.setState({ deleteModal: true });
  };

  closeDeleteModal = () => {
    this.setState({ deleteModal: false });
  };

  onProductError = () => {
    this.props.history.replace(SCREENS.NOT_FOUND());
  };

  fetchCashbackInfo = async () => {
    try {
      this.setState({ cashbackInfoLoading: true });
      const cashbackInfo = await requests.fetchCashbackInfo(this.state.product?.id, branchStore.branch.id);
      this.setState({ cashbackInfoData: cashbackInfo, cashbackInfoLoading: false });
    } catch (error: any) {
      this.setState({ cashbackInfoLoading: false });
      console.error({ error });
    }
  };

  fetchProduct = async () => {
    try {
      const product = await requests.fetchProduct(
        Number(this.props.match.params.id),
        this.props.location.state?.product
      );
      if (!product) {
        this.onProductError();
      }

      this.logEvent(product);
      this.setState({ product }, () => {
        this.validateModifiers();
        if (this.state.product?.type === PRODUCT_TYPE.MEAL_COMBO) this.setDefaultSelectedItems();
      });
    } catch (error: any) {
      console.error({ error });
      this.onProductError();
    }
  };

  validateModifiers = () => {
    if (!this.state.product) return;
    const errors = this.state.product.modifiers.reduce(
      (errors: IProductScreenState['errors'], modifier: IProductModifier): IProductScreenState['errors'] => {
        const valid = modifier.multi_choice
          ? validateMultiChoiseModifier(modifier)
          : validateSingleChoiseModifier(modifier);
        if (!valid) {
          errors.push(modifier.id);
        }
        return errors;
      },
      []
    );
    this.setState({ errors });
  };

  setDefaultSelectedItems = (_product?: IProduct) => {
    const { product } = this.state || _product;
    if (!product?.items?.length) return;

    // if not selected, then select is_default
    const isSelected: boolean = product.items.some((item) => item.is_selected);
    if (isSelected) return;

    const selectedtems: IProductInsideCombo[] = product.items?.map((item) =>
      item.is_default ? { ...item, is_selected: 1 } : item
    );

    this.setState({ product: { ...product, items: selectedtems, size: getFirstCorrectSize(product) } });
  };

  onModifierChange = (modifier: IProductModifier) => {
    this.setState(
      (state) => ({
        ...state,
        product: state.product
          ? {
              ...state.product,
              modifiers: state.product.modifiers.map((_modifier: IProductModifier) => {
                if (_modifier.id === modifier.id) return modifier;
                return _modifier;
              }),
            }
          : undefined,
      }),
      this.validateModifiers
    );
  };

  onDealChange = (item: IProductInsideCombo) => {
    this.setState((state) => ({
      ...state,
      product: state.product
        ? {
            ...state.product,
            items: state.product.items
              ? state.product.items.map((_item: IProductInsideCombo) => ({
                  ..._item,
                  is_selected: Number(_item.id === item.id),
                }))
              : state.product.items,
          }
        : undefined,
    }));
  };

  changeQuantity = (value: number) => {
    this.setState((state) => ({ product: state.product ? { ...state.product, quantity: value } : undefined }));
  };

  onSizeChange = (size: IProductSize) => {
    this.setState((state) => ({ product: state.product ? { ...state.product, size } : undefined }));
  };

  onExclusionsChange = (exclusions: IProductExclusion[]) => {
    this.setState((state) => ({ product: state.product ? { ...state.product, exclusions } : undefined }));
  };

  onNotesChanged = (notes: string) => {
    this.setState((state) => ({ product: state.product ? { ...state.product, notes } : undefined }));
  };

  onSelectMealItem = (item: IProductInsideCombo) => {
    const selectItemHandler = (_item: IProductInsideCombo): IProductInsideCombo => {
      if (isSameComboItems(_item, item)) return { ..._item, is_selected: 1 };
      if (isSameComboItemsSizeGroup(_item, item)) return { ..._item, is_selected: 0 };
      return _item;
    };

    const newItems: IProductInsideCombo[] = this.state.product?.items?.map((_item) => selectItemHandler(_item)) || [];
    if (!newItems.length) return;

    this.setState((state) => ({ product: state.product ? { ...state.product, items: newItems } : undefined }));
  };

  removeProductFromCart = () => {
    const { product } = this.state;
    if (product) {
      cartStore.remove(product);
      this.closeModal();
    }
  };

  setNeedScrollToError = (need: boolean) => {
    this.setState({ needScrollToError: need });
  };

  get cashbackInfo(): IProductCashbackInfo {
    const { product, cashbackInfoData, cashbackInfoLoading } = this.state;
    const shouldShowCashback = cashbackInfoData?.id != null || cashbackInfoLoading;

    return {
      name: cashbackInfoData?.name,
      description: cashbackInfoData?.description,
      amount: calcProductCashbackAmount(product, cashbackInfoData),
      loading: cashbackInfoLoading,
      visible: shouldShowCashback,
    };
  }

  renderImage = () => {
    return (
      <ProductImagesBlock
        product={this.state.product}
        updateProduct={this.props.location.state?.updateProduct && cartStore.isEditable}
        openDeleteModal={this.openDeleteModal}
      />
    );
  };

  renderSelectors = () => {
    const { product, errors, needScrollToError } = this.state;
    return (
      <ProductSelectorsBlock
        product={product}
        errors={errors}
        needScrollToError={needScrollToError}
        onModifierChange={this.onModifierChange}
        setNeedScrollToError={this.setNeedScrollToError}
        onExclusionsChange={this.onExclusionsChange}
        onNotesChanged={this.onNotesChanged}
        readonly={!cartStore.isEditable || (cartStore.isSMS && !product?.hash)}
      />
    );
  };

  renderInfo = (mdScreen?: boolean) => {
    return (
      <ProductInfoBlock
        mdScreen={mdScreen}
        product={this.state.product}
        cashback={this.cashbackInfo}
        onNotesChanged={this.onNotesChanged}
        onSizeChange={this.onSizeChange}
      />
    );
  };

  renderSubmit = () => {
    const { product, errors, needScrollToError } = this.state;
    return (
      <ProductSubmitBlock
        product={product}
        cashback={this.cashbackInfo}
        errors={errors}
        needScrollToError={needScrollToError}
        closeModal={this.closeModal}
        onSizeChange={this.onSizeChange}
        changeQuantity={this.changeQuantity}
        setNeedScrollToError={this.setNeedScrollToError}
      />
    );
  };

  renderItemsBlock = () => {
    if (this.state.product?.type === PRODUCT_TYPE.MEAL_COMBO) {
      return (
        <MealComboItemsBlock
          product={this.state.product}
          selectItem={this.onSelectMealItem}
          onNotesChanged={this.onNotesChanged}
        />
      );
    }

    return (
      <DealComboItemsBlock
        product={this.state.product}
        onNotesChanged={this.onNotesChanged}
        onDealChange={this.onDealChange}
      />
    );
  };

  public render() {
    const { product, deleteModal, isOpen } = this.state;
    const isCombo: boolean = !!this.state.product?.type;

    return (
      <Modal className="product-modal" isOpen={isOpen} onRequestClose={this.closeModal} withCloseIcon>
        <article className="product fadein">
          <ResponsiveContent max={WINDOW_SIZES.sm}>
            <div className="close_modal" onClick={this.closeModal} />
          </ResponsiveContent>
          <div className="d-flex flex-column flex-lg-row full-width" style={{ height: 'inherit' }}>
            <ResponsiveContent min={WINDOW_SIZES.md}>
              <div className={`d-flex flex-column justify-content-between ${isCombo ? 'w-42' : 'w-50'}`}>
                {this.renderImage()}
                {!product?.type ? this.renderSelectors() : this.renderInfo(true)}
              </div>
              <div className={`d-flex flex-column justify-content-between ${isCombo ? 'w-58' : 'w-50'}`}>
                {!product?.type ? this.renderInfo(true) : this.renderItemsBlock()}
                {this.renderSubmit()}
              </div>
            </ResponsiveContent>

            <ResponsiveContent min={WINDOW_SIZES.sm} max={WINDOW_SIZES.md}>
              <div
                className={`d-flex flex-column full-width ${product?.type ? '' : 'justify-content-between'}`}
                style={{ overflowY: 'auto', height: 'inherit' }}
              >
                {this.renderImage()}
                <div className="d-flex flex-row">
                  {!product?.type ? (
                    <>
                      <div className="d-flex flex-column half-width justify-content-between">{this.renderInfo()}</div>
                      <div className="d-flex flex-column half-width justify-content-between">
                        {this.renderSelectors()}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="d-flex flex-column full-width justify-content-between">{this.renderInfo()}</div>
                    </>
                  )}
                </div>
                {!product?.type ? null : this.renderItemsBlock()}
                {this.renderSubmit()}
              </div>
            </ResponsiveContent>

            <ResponsiveContent max={WINDOW_SIZES.sm}>
              <div
                className={`d-flex flex-column full-width ${product?.type ? '' : 'justify-content-between'}`}
                style={{ overflowY: 'auto', height: 'inherit' }}
              >
                {this.renderImage()}
                {this.renderInfo()}
                {!product?.type ? this.renderSelectors() : this.renderItemsBlock()}
                {this.renderSubmit()}
              </div>
            </ResponsiveContent>
          </div>
        </article>

        <ProductDeleteModal
          visible={deleteModal}
          onClose={this.closeDeleteModal}
          onSubmit={this.removeProductFromCart}
        />
      </Modal>
    );
  }
}
